import React, { useMemo } from "react";
import { Check, ChefHat, Layers3 } from "lucide-react";
import { COLORS } from "../../../constants";
import { BordeiroResumoResponse } from "../../../services/bordeiroService";
import type { PendingCard } from "../context/BordeiroContext";
import {
  isPedidoSalaoFoodfly,
  resolveSalaoProducaoLabels,
  SALAO_CARD_SURFACE_CLASS,
} from "../../Painel/utils/origemPainel";
import SalaoProducaoOrderHeader from "../../../components/SalaoProducaoOrderHeader";

interface BordeiroKanbanProps {
  resumo: BordeiroResumoResponse | null;
  pendingCards: PendingCard[];
  canFinalizar: boolean;
  onFinalizar: (card: PendingCard) => void;
}

const normalizeBordaKey = (s: string) =>
  (s || "").trim().toLowerCase().replace(/\s+/g, " ");

const BORDA_EM_FALTA_PULSE_CSS = `
@keyframes bordaEmFaltaPulse {
  0%, 100% { background-color: ${COLORS.ERROR_LIGHT}; border-color: ${COLORS.ERROR_DARK}; box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.3); }
  50% { background-color: ${COLORS.ERROR_LIGHT_ALT}; border-color: ${COLORS.ERROR_DARKER}; box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.4); }
}
.borda-em-falta-pulse {
  animation: bordaEmFaltaPulse 1.2s ease-in-out infinite;
}
`;

const BordeiroKanban: React.FC<BordeiroKanbanProps> = ({
  resumo,
  pendingCards,
  canFinalizar,
  onFinalizar,
}) => {
  const bordasEmFaltaSet = useMemo(() => {
    const list = resumo?.bordasEmFalta ?? [];
    return new Set(list.map(normalizeBordaKey));
  }, [resumo?.bordasEmFalta]);

  const temAlgumTrabalho =
    (resumo?.itens?.length ?? 0) > 0 ||
    (resumo?.paozinhos?.itens?.length ?? 0) > 0;

  return (
    <div className="flex-1 overflow-y-auto bg-neutral-100 p-3">
      <style>{BORDA_EM_FALTA_PULSE_CSS}</style>
      {!resumo || !temAlgumTrabalho ? (
        <div className="flex h-full flex-col items-center justify-center text-neutral-400">
          <Layers3 size={48} className="mb-3 opacity-30" />
          <p className="text-lg font-medium">Sem bordas para organizar</p>
          <p className="text-sm">
            Quando novos pedidos com borda ou pãozinho chegarem, eles aparecerão
            aqui.
          </p>
        </div>
      ) : pendingCards.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center text-neutral-400">
          <Check size={48} className="mb-3 opacity-30" />
          <p className="text-lg font-medium">
            Nenhuma borda de pedido pendente
          </p>
          <p className="mt-1 max-w-md text-center text-sm">
            Com estoque confirmado no Painel Bordeiro, os pedidos são abatidos
            sozinhos até 80% do planejado. Cards de pedido voltam a aparecer
            depois disso.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {pendingCards.map((card) => {
            const isPaozinho = card.kind === "paozinho";
            const isSalaoPaozinho =
              isPaozinho &&
              isPedidoSalaoFoodfly({
                origem: card.referencia?.origem,
              });
            const isSalaoBorda =
              !isPaozinho &&
              (card.referencia?.filaSalaoSalgadaId != null ||
                card.referencia?.filaSalaoDoceId != null);
            const isSalaoCard = isSalaoPaozinho || isSalaoBorda;
            const isEmFalta =
              !isPaozinho &&
              bordasEmFaltaSet.has(normalizeBordaKey(card.bordaNome));
            const badgeTamanho = isPaozinho
              ? "Pãozinho"
              : card.referencia?.tamanho || "--";
            const salaoLabels = isSalaoPaozinho
              ? resolveSalaoProducaoLabels({
                  mesaId: card.referencia?.salaoMesaId,
                  comandaId: card.referencia?.salaoComandaId,
                  identificador: card.referencia?.salaoIdentificador,
                  displayId: card.referencia?.displayId,
                })
              : null;

            return (
              <div
                key={card.cardId}
                data-salao-card={isSalaoCard ? "" : undefined}
                className={`flex h-[240px] w-[230px] flex-col rounded-lg border-2 p-3 shadow-sm ${
                  isEmFalta
                    ? "borda-em-falta-pulse border-red-600 bg-red-100"
                    : isSalaoCard
                      ? SALAO_CARD_SURFACE_CLASS
                      : isPaozinho
                        ? "border-amber-200 bg-amber-50/80"
                        : "border border-neutral-200 bg-white"
                }`}
              >
                <div className="flex shrink-0 items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 [[data-salao-card]_&]:text-white/70">
                    {isPaozinho ? "Pãozinho pendente" : "Borda pendente"}
                  </p>
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-sm font-semibold ${
                      isSalaoCard
                        ? "border-white/40 bg-white/20 text-white"
                        : "border-amber-200 bg-amber-100 text-amber-800"
                    }`}
                  >
                    {badgeTamanho}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 min-h-[2.5rem] shrink-0 text-base font-bold text-neutral-800 [[data-salao-card]_&]:text-white">
                  {card.nome}
                </p>
                <div className="min-h-0 flex-1 overflow-hidden">
                  <div className="mt-1">
                    {isPaozinho ? (
                      <SalaoProducaoOrderHeader
                        labels={salaoLabels}
                        fallbackBadge={
                          isSalaoPaozinho
                            ? undefined
                            : card.referencia?.displayId
                              ? `Pedido #${card.referencia.displayId}`
                              : "Pedido --"
                        }
                        showSalaoTag={isSalaoPaozinho}
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {isSalaoBorda ? (
                          <span className="inline-flex items-center rounded-full border border-emerald-400 bg-emerald-600 px-2.5 py-1 text-sm font-semibold text-white">
                            Salão
                          </span>
                        ) : null}
                        <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-sm font-semibold text-slate-800">
                          Pedido{" "}
                          {card.referencia?.displayId
                            ? `#${card.referencia.displayId}`
                            : "--"}
                        </span>
                        {(card.referencia?.filaSalgadaId != null ||
                          card.referencia?.filaDoceId != null) && (
                          <span
                            className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800"
                            title="ID da fila (pizzaiolo-integracao)"
                          >
                            Fila #
                            {card.referencia?.filaSalgadaId ??
                              card.referencia?.filaDoceId}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {card.referencia?.observation && (
                    <p
                      className={`mt-2 line-clamp-2 rounded border px-2 py-1 text-xs font-medium ${
                        isSalaoCard
                          ? "border-white/30 bg-white/15 text-white"
                          : "border-amber-200 bg-amber-50 text-amber-800"
                      }`}
                    >
                      {card.referencia.observation}
                    </p>
                  )}
                </div>
                {card.kind === "borda" &&
                  card.referencia?.assignedToUserNome && (
                    <div className="mt-auto flex shrink-0 items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1.5">
                      <ChefHat
                        size={14}
                        className="shrink-0 text-primary"
                        strokeWidth={2}
                      />
                      <span className="min-w-0 truncate text-xs font-medium text-neutral-700">
                        {card.referencia.assignedToUserNome}
                      </span>
                    </div>
                  )}
                {canFinalizar && (
                  <button
                    type="button"
                    onClick={() => onFinalizar(card)}
                    className="mt-2 flex w-full shrink-0 items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                  >
                    <Check size={16} />
                    Concluir
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BordeiroKanban;
